import { createRoot } from "react-dom/client";
import type { LabResultLine } from "@/core/laboratory/types";
import {
  formatLabAbnormalSummary,
  getLabResultComment,
  labOrderConsultationLabel,
  LAB_RESULT_FLAG_LABELS,
  LAB_STATUS_LABELS,
  type LabOrder,
} from "@/core/laboratory/types";
import { getPatientById } from "@/core/patients/patient-store";
import { patientFullName, patientGenderLabel } from "@/core/patients/types";

interface LabResultPrintSheetProps {
  order: LabOrder;
}

function printFlagClass(flag: LabResultLine["flag"]): string {
  if (flag === "critical" || flag === "high") return "lab-print-flag lab-print-flag--danger";
  if (flag === "low") return "lab-print-flag lab-print-flag--warning";
  if (flag === "normal") return "lab-print-flag lab-print-flag--ok";
  return "";
}

function LabResultPrintTable({ lines }: { lines: LabResultLine[] }) {
  return (
    <table className="table table-sm lab-print-table mb-0">
      <thead>
        <tr>
          <th>Paramètre</th>
          <th>Résultat</th>
          <th>Unité</th>
          <th>Valeurs de référence</th>
          <th>Interprétation</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={line.id} className={printFlagClass(line.flag)}>
            <td>{line.parameter || "—"}</td>
            <td className="fw-semibold">{line.value || "—"}</td>
            <td>{line.unit || "—"}</td>
            <td className="text-muted">{line.referenceRange || "—"}</td>
            <td>{line.flag ? LAB_RESULT_FLAG_LABELS[line.flag] : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LabResultPrintSheet({ order }: LabResultPrintSheetProps) {
  const patient = getPatientById(order.patientId);
  const validatedLabel = order.resultAt
    ? new Date(order.resultAt).toLocaleString("fr-FR")
    : new Date().toLocaleString("fr-FR");
  const abnormalSummary = formatLabAbnormalSummary(order.resultLines);
  const comment = getLabResultComment(order);

  return (
    <div className="ph-invoice-print-sheet">
      <div className="ph-invoice-header">
        <div>
          <div className="ph-invoice-brand">ITaaS Hospitol</div>
          <div className="ph-invoice-sub">Laboratoire — compte-rendu d&apos;analyse</div>
        </div>
        <div className="ph-invoice-meta text-end">
          <div className="ph-invoice-title">RÉSULTAT</div>
          <div className="fw-semibold">{order.orderNumber}</div>
          <div className="small text-muted">Validé le {validatedLabel}</div>
          <div className="small text-muted">{LAB_STATUS_LABELS[order.status]}</div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="ph-invoice-section-title">Patient</div>
          <div className="fw-semibold">{patient ? patientFullName(patient) : "—"}</div>
          {patient && (
            <>
              <div className="small text-muted">Dossier {patient.fileNumber}</div>
              {patient.birthDate && (
                <div className="small text-muted">
                  Né(e) le {new Date(patient.birthDate).toLocaleDateString("fr-FR")}
                  {patient.gender ? ` · ${patientGenderLabel(patient.gender)}` : ""}
                </div>
              )}
              {patient.phone && <div className="small text-muted">Tél. {patient.phone}</div>}
            </>
          )}
        </div>
        <div className="col-md-6">
          <div className="ph-invoice-section-title">Demande</div>
          <div className="fw-semibold">{order.testName}</div>
          {order.clinicalNotes && (
            <div className="small text-muted mt-1">Indication : {order.clinicalNotes}</div>
          )}
          {order.prescriber && (
            <div className="small text-muted mt-1">Prescripteur : {order.prescriber}</div>
          )}
          {order.consultationId && (
            <div className="small text-muted mt-1">
              Origine : {labOrderConsultationLabel(order.consultationId)}
            </div>
          )}
        </div>
      </div>

      {abnormalSummary && (
        <div className="lab-print-alert mb-4">
          <div className="fw-semibold">Valeurs hors norme</div>
          <div className="small">{abnormalSummary}</div>
        </div>
      )}

      <div className="ph-invoice-section-title">Résultats détaillés</div>
      {order.resultLines.length > 0 ? (
        <div className="mb-4">
          <LabResultPrintTable lines={order.resultLines} />
          {comment && (
            <div className="mt-3">
              <div className="ph-invoice-section-title">Commentaire biologiste</div>
              <div className="small border rounded p-3" style={{ whiteSpace: "pre-wrap" }}>
                {comment}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="border rounded p-3 mb-4" style={{ whiteSpace: "pre-wrap", minHeight: "8rem" }}>
          {order.resultText || "—"}
        </div>
      )}

      <div className="row g-3 mt-4 pt-3 border-top">
        <div className="col-md-6">
          <div className="small text-muted">Biologiste / validateur</div>
          <div style={{ minHeight: "2.5rem" }} className="border-bottom mt-4" />
        </div>
        <div className="col-md-6 text-end">
          <div className="small text-muted">Date de validation</div>
          <div className="fw-medium">{validatedLabel}</div>
        </div>
      </div>

      <div className="small text-muted mt-4">
        Document généré par ITaaS Hospitol — usage médical interne. Les valeurs doivent être
        interprétées dans le contexte clinique du patient.
      </div>
    </div>
  );
}

export function printLabResult(order: LabOrder): void {
  const portal = document.createElement("div");
  portal.className = "ph-print-portal";
  portal.setAttribute("aria-hidden", "true");
  document.body.appendChild(portal);

  const root = createRoot(portal);
  root.render(<LabResultPrintSheet order={order} />);

  window.setTimeout(() => {
    window.print();
    root.unmount();
    portal.remove();
  }, 150);
}

export { LabResultPrintSheet };
